import { c as _c } from "react/compiler-runtime";
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type React from 'react';
import { forwardRef, useId, useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useFormattedPhoneValues } from '../../../../hooks/use-formatted-values';
import { useInputMask } from '../../../../hooks/use-input-mask';
import Label from '../../../label';
import type { PhoneInputProps } from '../../phone-input.types';
import { getNationalNumber } from '../../phone-input.utils';
import CountryPicker from './components/country-picker';
import { defaultPreference, preferences } from './input.constants';
type InputProps = PhoneInputProps & {
  countryCode: Iso3166TwoDigitCountryCode;
  prefix: string;
  selectTrigger?: {
    isOpen?: boolean;
    onClick?: () => void;
  };
};
const Input = forwardRef<HTMLInputElement, InputProps>((t0, ref) => {
  const $ = _c(60);
  let baseId;
  let countryCode;
  let defaultValue;
  let label;
  let onBlur;
  let onChange;
  let onChangeText;
  let prefix;
  let props;
  let selectTrigger;
  let t1;
  let t2;
  let value;
  if ($[0] !== t0) {
    const {
      countryCode: t3,
      defaultValue: t4,
      disabled: t5,
      hasError: t6,
      id: t7,
      label: t8,
      onBlur: t9,
      onChange: t10,
      onChangeText: t11,
      onFocus,
      prefix: t12,
      selectTrigger: t13,
      size,
      value: t14,
      ...t15
    } = t0;
    countryCode = t3;
    defaultValue = t4;
    t1 = t5;
    t2 = t6;
    baseId = t7;
    label = t8;
    onBlur = t9;
    onChange = t10;
    onChangeText = t11;
    prefix = t12;
    selectTrigger = t13;
    value = t14;
    props = t15;
    $[0] = t0;
    $[1] = baseId;
    $[2] = countryCode;
    $[3] = defaultValue;
    $[4] = label;
    $[5] = onBlur;
    $[6] = onChange;
    $[7] = onChangeText;
    $[8] = prefix;
    $[9] = props;
    $[10] = selectTrigger;
    $[11] = t1;
    $[12] = t2;
    $[13] = value;
  } else {
    baseId = $[1];
    countryCode = $[2];
    defaultValue = $[3];
    label = $[4];
    onBlur = $[5];
    onChange = $[6];
    onChangeText = $[7];
    prefix = $[8];
    props = $[9];
    selectTrigger = $[10];
    t1 = $[11];
    t2 = $[12];
    value = $[13];
  }
  const disabled = t1 === undefined ? false : t1;
  const hasError = t2 === undefined ? false : t2;
  const [isFocused, setFocus] = useState(false);
  const localRef = useRef(null);
  const internalId = useId();
  const id = baseId || internalId;
  const countryPreferences = preferences[countryCode] || defaultPreference;
  let t3;
  if ($[14] !== onBlur) {
    t3 = event => {
      setFocus(false);
      onBlur?.(event);
    };
    $[14] = onBlur;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const handleBlur = t3;
  let t4;
  if ($[16] !== onBlur) {
    t4 = event_0 => {
      setFocus(true);
      onBlur?.(event_0);
    };
    $[16] = onBlur;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleFocus = t4;
  const {
    inputRef: maskInputRef,
    handleChange: handleMaskChange
  } = useInputMask(countryPreferences.mask);
  let t5;
  if ($[18] !== countryPreferences.mask || $[19] !== defaultValue || $[20] !== prefix || $[21] !== value) {
    t5 = {
      value,
      defaultValue,
      mask: countryPreferences.mask,
      prefix,
      getNationalNumber
    };
    $[18] = countryPreferences.mask;
    $[19] = defaultValue;
    $[20] = prefix;
    $[21] = value;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const {
    formattedValue,
    formattedDefaultValue
  } = useFormattedPhoneValues(t5);
  let t6;
  if ($[23] !== handleMaskChange || $[24] !== onChange || $[25] !== onChangeText || $[26] !== prefix) {
    t6 = event_1 => {
      handleMaskChange(event_1);
      const formattedValue_0 = event_1.currentTarget.value;
      const inputValueWithCountryCode = `${prefix} ${formattedValue_0}`;
      const modifiedEvent = {
        ...event_1,
        target: {
          ...event_1.target,
          value: inputValueWithCountryCode
        }
      };
      onChange?.(modifiedEvent);
      onChangeText?.(modifiedEvent.target.value);
    };
    $[23] = handleMaskChange;
    $[24] = onChange;
    $[25] = onChangeText;
    $[26] = prefix;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const handleChange = t6;
  let t7;
  if ($[28] !== id || $[29] !== label) {
    t7 = label ? <Label htmlFor={id}>{label}</Label> : null;
    $[28] = id;
    $[29] = label;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  const t8 = "fp-input fp-phone-input fp-phone-input-container fp-custom-appearance";
  const t9 = "default";
  const t10 = () => {
    localRef.current?.focus();
  };
  const t11 = selectTrigger?.onClick;
  let t12;
  if ($[31] !== countryCode || $[32] !== disabled || $[33] !== prefix || $[34] !== t11) {
    t12 = <CountryPicker code={countryCode} disabled={disabled} onClick={t11} prefix={prefix} />;
    $[31] = countryCode;
    $[32] = disabled;
    $[33] = prefix;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = "tel-national";
  const t14 = formattedDefaultValue || undefined;
  const t15 = mergeRefs([localRef, maskInputRef, ref]);
  const t16 = formattedValue || undefined;
  let t17;
  if ($[36] !== countryCode || $[37] !== countryPreferences.placeholder || $[38] !== disabled || $[39] !== handleBlur || $[40] !== handleChange || $[41] !== handleFocus || $[42] !== hasError || $[43] !== id || $[44] !== isFocused || $[45] !== props || $[46] !== t14 || $[47] !== t15 || $[48] !== t16) {
    t17 = <input {...props} autoComplete={t13} data-has-error={hasError} data-has-focus={isFocused} defaultValue={t14} disabled={disabled} ref={t15} id={id} key={countryCode} onBlur={handleBlur} onChange={handleChange} onFocus={handleFocus} placeholder={countryPreferences.placeholder} type="tel" value={t16} className="fp-input fp-phone-input fp-phone-input-field fp-custom-appearance" />;
    $[36] = countryCode;
    $[37] = countryPreferences.placeholder;
    $[38] = disabled;
    $[39] = handleBlur;
    $[40] = handleChange;
    $[41] = handleFocus;
    $[42] = hasError;
    $[43] = id;
    $[44] = isFocused;
    $[45] = props;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== disabled || $[51] !== hasError || $[52] !== isFocused || $[53] !== t10 || $[54] !== t12 || $[55] !== t17) {
    t18 = <div className={t8} data-size={t9} data-has-error={hasError} data-has-focus={isFocused} data-disabled={disabled} onClick={t10}>{t12}{t17}</div>;
    $[50] = disabled;
    $[51] = hasError;
    $[52] = isFocused;
    $[53] = t10;
    $[54] = t12;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  let t19;
  if ($[57] !== t18 || $[58] !== t7) {
    t19 = <div>{t7}{t18}</div>;
    $[57] = t18;
    $[58] = t7;
    $[59] = t19;
  } else {
    t19 = $[59];
  }
  return t19;
});
export default Input;